import { BudgetPeriodEnum, CardStatusEnum } from "@finway-group/shared/lib/models"
import { composeWithDevTools } from "@redux-devtools/extension"
import localForage from "localforage"
import { type Action, applyMiddleware, createStore, type Dispatch, type Store } from "redux"
import { createMigrate, persistReducer, persistStore } from "redux-persist"
import thunk, { type ThunkAction, type ThunkDispatch } from "redux-thunk"
import { PeriodRangeOptionEnum } from "~Features/pages/costCenters/costCenterDetails/costCenterPeriodOption.interface"
import { invalidateDeliveryNoteCache } from "~Features/pages/deliveryNotes/queries/deliveryNote.queries"
import { invalidatePurchaseOrderCache } from "~Features/pages/purchaseOrders/queries/purchaseOrder.queries"
import { invalidateEmployees } from "~Shared/queries/employee.queries"
import { invalidateExpense } from "~Shared/queries/expenses/expense.queries"
import { invalidateAllOposLists } from "~Shared/queries/expenses/oposList.invalidation"
import { invalidateFilters } from "~Shared/queries/filter.queries"
import { invalidateNotifications } from "~Shared/queries/notification.queries"
import { removeTableColumnViews } from "~Shared/queries/tableColumnView.queries"
import { invalidateVendorLists, invalidateVendors } from "~Shared/queries/vendors/vendor.queries"
import { type AnalyticsActions } from "~Shared/store/actions/analytics/analyticsTypes"
import { type AuthActions } from "~Shared/store/actions/auth/authTypes"
import { type CompanyActions, CompanyActionTypes } from "~Shared/store/actions/company/companyTypes"
import { type CostCenterActions } from "~Shared/store/actions/costCenter/costCenterTypes"
import { type CsvTemplateActions } from "~Shared/store/actions/csvTemplates/csvTemplateTypes"
import { type EmployeeActions, EmployeeActionTypes } from "~Shared/store/actions/employee/employeeTypes"
import { type ExpenseActions, ExpenseActionTypes } from "~Shared/store/actions/expense/expenseTypes"
import { type FetcherActions } from "~Shared/store/actions/fetcher/fetcherTypes"
import { type ImportActions } from "~Shared/store/actions/import/importTypes"
import { type TableActions } from "~Shared/store/actions/tables/tableTypes"
import { rootReducer } from "~Shared/store/reducers/rootReducer"
import { initialTableConfigData } from "~Shared/store/reducers/tableConfigReducer"
import { createInitialTable, initialTablesState } from "~Shared/store/reducers/tableReducer"
import { type RootState } from "~Shared/store/rootState.interface"
import { createBroadcastMiddleware, initBroadcastListener } from "~Shared/store/utils/broadcastMiddleware"
import { isNodeEnvProd } from "~Shared/utils/helper.utils"
import { TableActionsTypes } from "./actions/tables/tableTypes.enum"

export type StoreActions =
    | AuthActions
    | CompanyActions
    | CostCenterActions
    | EmployeeActions
    | ExpenseActions
    | FetcherActions
    | AnalyticsActions
    | TableActions
    | ImportActions
    | CsvTemplateActions

export type ThunkResult<R> = ThunkAction<Promise<R>, RootState, undefined, StoreActions>

export type ThunkDispatchResult = ThunkDispatch<RootState, undefined, StoreActions>

const db = localForage.createInstance({
    name: "IndexedDB",
})

/**
 * Persist the redux state using IndexedDB
 */
const initializeStorage = () => ({
    db,
    getItem: db.getItem,
    setItem: db.setItem,
    removeItem: db.removeItem,
})

export const clearStore = () => {
    db.clear()
}

const migrations = {
    0: (state: any) => ({
        ...state,
        analytics: {
            ...state.analytics,
            costCenterBudgetUsageBarGraphOptions: {
                startDate: "",
                endDate: "",
                periodRangeOption: PeriodRangeOptionEnum.LAST_12_PERIODS,
                viewPeriod: BudgetPeriodEnum.MONTHLY,
            },
        },
        company: {
            items: [],
            item: {},
        },
        workflows: {
            items: [],
            item: undefined,
        },
        tableConfig: {
            ...state.tableConfig,
            todoPurchaseApprovalTable: initialTableConfigData,
            todoInvoiceApprovalTable: initialTableConfigData,
            todoInvoiceAndPurchaseApprovalTable: initialTableConfigData,
            todoDocsNeededTable: initialTableConfigData,
            todoApprovalPendingTable: initialTableConfigData,
            workflowTable: initialTableConfigData,
            archiveTable: initialTableConfigData,
        },
    }),
    // table refactor update
    1: (state: any) => {
        const newTableConfig = {
            ...state.tableConfig,
            inboxInvoiceTable: state.tableConfig.invoiceInboxTable,
            inboxInvoiceModalInvoicesTable: state.tableConfig.invoiceInboxModalInvoicesTable,
            inboxInvoiceModalExpensesTable: state.tableConfig.invoiceInboxModalExpensesTable,
            payAndExportToBePaidTable: initialTableConfigData,
            payAndExportToBeExportedTable: initialTableConfigData,
            doneTable: initialTableConfigData,
        }
        delete newTableConfig.invoiceInboxTable
        delete newTableConfig.invoiceInboxModalInvoicesTable
        delete newTableConfig.invoiceInboxModalExpensesTable
        delete newTableConfig.payExportTable
        delete newTableConfig.allTable
        return {
            ...state,
            tableConfig: newTableConfig,
            tables: initialTablesState,
        }
    },
    // update card table initial values
    2: (state: any) => ({
        ...state,
        tables: { ...state.tables, cardTable: { ...state.tables.cardTable, data: { ...state.tables.cardTable.data, filter: { status: CardStatusEnum.ACTIVE } } } },
    }),
    // add transactionCategory to store and transactionCategoriesTable to tables store
    3: (state: any) => ({
        ...state,
        transactionCategories: { items: [] },
        tables: { ...state.tables, transactionCategoriesTable: createInitialTable() },
    }),
    // add card transaction table to table store
    4: (state: any) => ({
        ...state,
        tables: { ...state.tables, cardTransactionsTable: initialTableConfigData },
    }),
    // add all tab table to table store
    5: (state: any) => ({
        ...state,
        tables: { ...state.tables, allRequestsTable: createInitialTable() },
    }),
    // add expense transaction table to table store
    6: (state: any) => ({
        ...state,
        tables: { ...state.tables, expenseTransactionsTable: createInitialTable({}, 5) },
    }),
    // add transactionArchiveTable to table store
    7: (state: any) => ({
        ...state,
        tables: { ...state.tables, transactionArchiveTable: createInitialTable() },
    }),
    // add transactionArchiveTable to table store
    8: (state: any) => ({
        ...state,
        tables: { ...state.tables, todoInvoiceAndPurchaseApprovalTable: createInitialTable() },
    }),
    // add draftTripFolderTable and attachExpenseTable to table store for trip folder specific tables
    9: (state: any) => ({
        ...state,
        tables: { ...state.tables, draftTripFolderTable: createInitialTable(), attachExpenseTable: createInitialTable },
    }),
    // change type of isWeavrAuthenticated to boolean
    10: (state: any) => ({
        ...state,
        auth: { ...state.auth, isWeavrAuthenticated: false },
    }),
    // add exportHistoryTable
    11: (state: any) => ({
        ...state,
        tables: { ...state.tables, exportHistoryTable: createInitialTable() },
    }),
    12: (state: any) => ({
        ...state,
        tables: { ...state.tables, transactionAllTable: createInitialTable() },
    }),
    // replace the shouldFetch boolean of every table with the fetch request counters
    13: (state: any) => ({
        ...state,
        tables: Object.fromEntries(
            Object.entries(state.tables ?? {}).map(([tableName, table]: [string, any]) => {
                const { shouldFetch, ...tableWithoutFlag } = table
                return [tableName, { ...tableWithoutFlag, fetchRequestId: shouldFetch ? 1 : 0, startedFetchRequestId: 0 }]
            }),
        ),
    }),
    // drop the employee tables now that react query holds their rows
    14: (state: any) => {
        const tables = { ...state.tables }
        delete tables.employeeTable
        delete tables.employeeVendorTable
        return { ...state, tables }
    },
}

const persistConfig = {
    key: "root",
    version: 14,
    storage: initializeStorage(),
    // TODO: re-enable if needed
    // stateReconciler: autoMergeLevel2, // see "Merge Process" section for details.
    migrate: createMigrate(migrations),
}

// currently we are transitioning from redux to react query.
// use this middleware to invalidate the cache so react query can refetch accordingly
const invalidateReactQueryCacheMiddleware = (store: Store) => (next: Dispatch<Action>) => async (action: StoreActions) => {
    switch (action.type) {
        case CompanyActionTypes.SELECT_COMPANY:
            await Promise.allSettled([
                invalidateNotifications(),
                invalidateVendorLists(),
                invalidateVendors(),
                invalidateEmployees(),
                invalidatePurchaseOrderCache(),
                invalidateDeliveryNoteCache(),
                invalidateAllOposLists(),
                invalidateFilters(),
                removeTableColumnViews(),
            ])
            break
        case EmployeeActionTypes.UPDATE_EMPLOYEE:
        case EmployeeActionTypes.SYNC_EMPLOYEE:
        case EmployeeActionTypes.NEW_USER:
        case EmployeeActionTypes.UPDATE_USER:
            // invalidate all employees queries. Even we only update one employee, it affects computed value like substitutedBy for other employees.
            await invalidateEmployees()
            break
        case ExpenseActionTypes.UPDATE_EXPENSE:
            await invalidateExpense(action.expense.id)
            break
        case TableActionsTypes.REFETCH_EXPENSE_RELATED_TABLES:
            await Promise.allSettled([invalidateAllOposLists()])
            break
        default:
            break
    }
    next(action)
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

// Whitelist of action types to broadcast across browser tabs
const broadcastWhitelist = [CompanyActionTypes.SELECT_COMPANY]

const middleware = [thunk, invalidateReactQueryCacheMiddleware, createBroadcastMiddleware(broadcastWhitelist)]

const traceComposeEnhancer = composeWithDevTools({ trace: true, traceLimit: 25 })

const enhancers = isNodeEnvProd ? applyMiddleware(...middleware) : traceComposeEnhancer(applyMiddleware(...middleware))

export const store = createStore(persistedReducer, enhancers)

initBroadcastListener(store)

export const persistor = persistStore(store)
